import { Link } from "react-router-dom";
import { ArrowRight, Wind, Truck, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { useRef } from "react";
import SEO from "@/components/SEO";
import { useHeroVariant } from "@/lib/useHeroVariant";
import { useHeroProduct } from "@/lib/useHeroProduct";
import { useCart } from "@/store/cart";
import { swayStyleFor } from "@/lib/swayStyle";

export default function Home() {
  const hero = useHeroVariant(null);
  const { cartId } = useCart();
  const heroProduct = useHeroProduct(cartId);
  const tiltRef = useRef(null);

  const handleTilt = (e) => {
    const el = tiltRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;   // -0.5 .. 0.5
    const y = (e.clientY - r.top) / r.height - 0.5;
    const rotY = x * 14;   // left/right tilt
    const rotX = -y * 12;  // up/down tilt
    el.style.transform = `perspective(900px) rotateX(${rotX.toFixed(2)}deg) rotateY(${rotY.toFixed(2)}deg)`;
  };
  const resetTilt = () => {
    const el = tiltRef.current;
    if (el) el.style.transform = "perspective(900px) rotateX(0deg) rotateY(0deg)";
  };
  const homeJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Booty Breeze",
    "url": "https://bootybreeze.com",
    "logo": "https://bootybreeze.com/apple-touch-icon.png",
    "description": "Small-batch, hand-poured adult (18+) car air fresheners. Six unapologetic scents shipped from the USA — free shipping over $10.",
    "email": "admin@bootybreeze.com",
    "sameAs": [],
    "areaServed": ["US", "CA", "GB", "AU"],
  };
  return (
    <div data-testid="home-page">
      <SEO
        title="Adult Car Air Fresheners — Small-Batch, Hand-Poured in the USA"
        description="Booty Breeze makes small-batch adult (18+) car air fresheners. Six bold, hand-poured scents that last 3–4 weeks. Free shipping over $10. Ships discreetly from the USA."
        path="/"
        jsonLd={homeJsonLd}
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none transition-[background-image] duration-500"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(5,5,5,0.35) 0%, rgba(5,5,5,0.60) 60%, #050505 100%), url(${hero.image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          data-testid={`hero-bg-${hero.hero_variant}`}
        />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-20 pb-32 sm:pt-32 sm:pb-40">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FF3D8B]/40 bg-[#FF3D8B]/10 text-[#FF3D8B] text-xs uppercase tracking-widest mb-8">
              <Sparkles size={12} /> Adults Only — 18+
            </div>
            <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.9] text-white">
              Ride like you <span className="text-neon-pink">mean it.</span>
            </h1>
            <p className="mt-8 text-white/70 max-w-xl text-base sm:text-lg leading-relaxed">
              Grown-up car air fresheners that hit harder than "new car scent."
              Six small-batch, hand-poured fragrances — bold enough to notice,
              built to last three to four weeks.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/shop"
                data-testid="hero-shop-btn"
                className="pulse-pink inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-wider text-sm hover:bg-[#ff5ba0] transition-colors"
              >
                Meet the Scents <ArrowRight size={18} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PERKS BAR */}
      <section className="border-y border-white/10 bg-black">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-8 grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-10">
          {[
            { icon: Wind, title: "3–4 Weeks", sub: "of long-lasting scent" },
            { icon: Truck, title: "Discreet Delivery", sub: "plain packaging, always" },
            { icon: Sparkles, title: "Small-Batch", sub: "premium fragrance oils" },
          ].map(({ icon: Icon, title, sub }) => (
            <div key={title} className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-full border border-[#FF3D8B]/40 text-[#FF3D8B] flex items-center justify-center">
                <Icon size={18} strokeWidth={1.75} />
              </div>
              <div>
                <div className="font-display text-lg text-white leading-none">{title}</div>
                <div className="text-white/50 text-xs mt-1 uppercase tracking-wider">{sub}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-14">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-display text-4xl sm:text-5xl text-white leading-tight">
              Six scents. <br />
              <span className="text-neon-purple">Zero apologies.</span>
            </h2>
            <p className="text-white/60 mt-6 max-w-md leading-relaxed">
              Six unapologetic scents, small-batch and cured for slow release.
              Pick one to match the mood — or grab two and unlock free shipping
              on the way to a whole new vibe.
            </p>
            {heroProduct.product && (
              <div className="mt-6 text-white/50 text-xs uppercase tracking-widest" data-testid={`home-hero-product-caption-${heroProduct.variant}`}>
                Today's featured scent — <span className="text-[#FF3D8B]">{heroProduct.product.name}</span>
              </div>
            )}
          </div>
          <Link
            to={heroProduct.product ? `/product/${heroProduct.product.id}` : "/shop"}
            onClick={heroProduct.trackClick}
            data-testid={`home-hero-product-link-${heroProduct.variant || "loading"}`}
            aria-label={heroProduct.product ? `Shop ${heroProduct.product.name}` : "Shop all"}
            className="relative aspect-square flex items-center justify-center overflow-visible group"
            onMouseMove={handleTilt}
            onMouseLeave={resetTilt}
          >
            <div
              ref={tiltRef}
              className="w-full h-full flex items-center justify-center transition-transform duration-200 ease-out will-change-transform"
              style={{ transformStyle: "preserve-3d" }}
            >
              {heroProduct.image ? (
                <img
                  src={heroProduct.image}
                  alt={heroProduct.product?.name || "Booty Breeze air freshener"}
                  data-testid={`home-hero-product-img-${heroProduct.variant}`}
                  className="sway-hang relative max-h-[92%] w-auto object-contain drop-shadow-[0_25px_55px_rgba(255,61,139,0.55)] group-hover:drop-shadow-[0_30px_65px_rgba(255,61,139,0.75)] transition-[filter] duration-500"
                  style={swayStyleFor(heroProduct.variant || "home")}
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                // graceful placeholder until the API resolves
                <div className="w-40 h-40 rounded-full bg-white/5 animate-pulse" data-testid="home-hero-product-loading" />
              )}
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
}
